import { defineMessages, FormattedMessage } from 'react-intl';
import { discardOutboxEntry, retryOutboxEntry, useOutbox } from '../offline/client';
import type { OutboxEntry } from '../offline/outbox';

const m = defineMessages({
  title: { id: 'outbox.title', defaultMessage: '{count, plural, one {Waiting to send} other {# waiting to send}}', description: 'Title of the queued-writes box on a decision page (planning-app.md §4.4 outbox)' },
  comment: { id: 'outbox.kind.comment', defaultMessage: 'Comment', description: 'Kind label of a queued write' },
  consent: { id: 'outbox.kind.consent', defaultMessage: 'Stance', description: 'Kind label of a queued consent (consent / object / abstain)' },
  verification: { id: 'outbox.kind.verification', defaultMessage: 'Verification result', description: 'Kind label of a queued verification result (pass / fail / blocked) from the verification desk' },
  failed: { id: 'outbox.failed', defaultMessage: 'Could not be sent ({reason}). It stays on this device until you retry or discard it.', description: 'Under a queued write the outbox gave up retrying; reason is the server status' },
  retry: { id: 'outbox.retry', defaultMessage: 'Retry', description: 'Button on a failed queued write' },
  discard: { id: 'outbox.discard', defaultMessage: 'Discard', description: 'Button on a failed queued write: drop it for good' },
});

/**
 * The writes queued for one subject (a decision): comments and consents alike, in the order
 * they were made, each marked as waiting or — after the automatic attempts — failed, with
 * Retry (same key) and Discard. Every entry that can reach the failed state has its recovery
 * here, whatever card it was written from.
 */
export function OutboxEntries({ subjectId }: { subjectId: string }) {
  const entries = useOutbox().filter((e) => e.subjectId === subjectId);
  if (entries.length === 0) return null;
  return (
    <div className="queued" role="status" data-testid="queued-comments">
      <b>
        <FormattedMessage {...m.title} values={{ count: entries.length }} />
      </b>
      {entries.map((e) => (
        <Entry key={e.id} entry={e} />
      ))}
    </div>
  );
}

function Entry({ entry: e }: { entry: OutboxEntry }) {
  const failed = e.state === 'failed';
  return (
    <span className={failed ? 'queued-failed' : undefined} data-testid={failed ? 'queued-failed' : 'queued-entry'} data-kind={e.kind}>
      <span>
        <small className="muted">
          <FormattedMessage {...(e.kind === 'consent' ? m.consent : e.kind === 'verification' ? m.verification : m.comment)} />
        </small>{' '}
        <span lang="en">{e.preview}</span>
      </span>
      {failed && (
        <>
          <small className="muted">
            <FormattedMessage {...m.failed} values={{ reason: e.lastError ?? '' }} />
          </small>
          <span className="row gap">
            <button type="button" className="btn sm" onClick={() => void retryOutboxEntry(e.id)} data-testid="queued-retry">
              <FormattedMessage {...m.retry} />
            </button>
            <button type="button" className="btn ghost sm" onClick={() => void discardOutboxEntry(e.id)} data-testid="queued-discard">
              <FormattedMessage {...m.discard} />
            </button>
          </span>
        </>
      )}
    </span>
  );
}
